import { type } from 'arktype';
import { booleanField, meta, numberField, stringField } from './schema-doc';
import { locationEntrySchema } from './work-location.model';

const sourceField = stringField(
  'Origin marker; absent means `manual`. A non-`manual` value on a read-only slot type (absence, holiday) is rejected with 400.',
  ['manual']
);
const exdatesField = type('string[]').configure(meta('Occurrence dates (YYYY-MM-DD) skipped by a recurring slot.', [['2026-05-01']]));
const translationsField = type('Record<string, unknown>').configure(
  meta('Per-language `text`/`description` map keyed by de, en, fr, it. Stored as sent, not interpreted on write.')
);
const isAllDayField = booleanField('True when the slot covers the whole day.');
const startTimeField = stringField('Start time `HH:mm` (`HH:mm:ss[.fff]` also accepted); used by the overlap check.', ['09:00']);
const endTimeField = stringField('End time, same formats as `startTime` and after it; `24:00` is allowed. Used by the overlap check.', [
  '10:00',
]);

const customPropertiesField = (examples?: unknown[]) =>
  type('Record<string, unknown>').configure(
    meta(
      'Custom-property values keyed by the definition `key`. Values are validated against the tenant custom-property definitions for `calendar-slot` entities; violations return 400 `Invalid custom property values`. Keys outside the slot type scope are rejected. Required values depend on `meetingType` via the definition `config.requiredForBookingTypes` and are enforced on create. On update, omitted keys stay unchanged, `null` for a key clears it, and only a required key sent empty or `null` is rejected. GET returns the same record.',
      examples
    )
  );

const bookingCustomPropertiesExample = {
  customerName: 'Ada Lovelace',
  phoneNumber: '+41 79 000 00 00',
  emailAddress: 'ada@example.com',
  meetingType: 'Video call',
  destinationSkillIds: ['3f2b8c1e-6d4a-4b7e-9a1c-5e8d2f0a7b34'],
  requiredLanguages: ['de', 'en'],
};

const commonDataFields = {
  'source?': sourceField,
  'customProperties?': customPropertiesField(),
  'exdates?': exdatesField,
  'text?': stringField('Slot title; used as Outlook subject and in notifications.', ['Consultation']),
  'title?': stringField('Fallback Outlook subject when `text` is empty.'),
  'description?': stringField('Free-form description.'),
  'icon?': stringField('Icon identifier shown for the slot.', ['event']),
  'translations?': translationsField,
} as const;

const requiredTimeFields = {
  startTime: startTimeField,
  endTime: endTimeField,
  'isAllDay?': isAllDayField,
} as const;

const locationEntriesField = (description: string) => locationEntrySchema.array().configure(meta(description));

const locationSlotDataFields = {
  ...commonDataFields,
  'source?': stringField(
    'Origin marker; absent means `manual`. A `system:*` source on a full-day location-override is resolved with special precedence.'
  ),
  'locationMeta?': type('unknown').configure(meta('Display snapshot of the location; stored as sent and never touched by the write path.')),
} as const;

const externalBookingSlotDataSchema = type({
  type: type("'external-booking'").configure(meta('Customer booking in a time range.')),
  ...commonDataFields,
  ...requiredTimeFields,
  'customProperties?': customPropertiesField([bookingCustomPropertiesExample]),
});

const teamMeetingSlotDataSchema = type({
  type: type("'team-meeting'").configure(meta('Team meeting in a time range.')),
  ...commonDataFields,
  ...requiredTimeFields,
});

const workLocationSlotDataSchema = type({
  type: type("'work-location'").configure(meta('Where the person works, as time ranges; usually recurring.')),
  ...locationSlotDataFields,
  entries: locationEntriesField('Location per time range of the day.'),
});

const locationOverrideSlotDataSchema = type({
  type: type("'location-override'").configure(
    meta('Replaces the work location of a day. Overwriting an existing location-override with other slot data is rejected with 400.')
  ),
  ...locationSlotDataFields,
  entries: locationEntriesField(
    'Location per time range. Each `locationId` must be a location assignment of the person valid on `dateFrom`, else 400.'
  ),
});

const absenceSlotDataSchema = type({
  type: type("'absence'").configure(
    meta('Absence of the person. Create and delete may be diverted to an approval request, in which case no slot row is written.')
  ),
  ...commonDataFields,
  absenceType: stringField('Absence type key, optionally prefixed with `absence:`. Holiday-flagged types are rejected with 400.', [
    'vacation',
  ]),
  portion: type("'full'|'fraction'|'hours'|'range'").configure(
    meta('How much of the day is absent; unknown values are treated as `full`.')
  ),
  isAllDay: isAllDayField,
  'startTime?': startTimeField,
  'endTime?': endTimeField,
  'fractionValue?': numberField('Day fraction for `portion: fraction`, e.g. 0.5.'),
  'durationMinutes?': numberField('Absent minutes for `portion: hours`.'),
  'absenceId?': stringField('Id linking the slot to its absence record; kept when sent, otherwise stamped by the server.'),
  'resolvedCustomPropertyLabels?': type('Record<string, unknown>').configure(
    meta('Response only: display labels of the custom property values, stamped by the server.')
  ),
  'isPending?': booleanField('Response only: true while the absence waits for approval.'),
  'requestId?': stringField('Response only: id of the pending approval request.'),
  'requestedByUserId?': stringField('Response only: user that requested the pending absence.'),
  'readOnly?': booleanField('Response only: true when the slot cannot be edited, e.g. while pending.'),
  'deepLink?': stringField('Response only: link to the pending approval request.'),
  'pendingKind?': stringField('Response only: kind of pending change, e.g. create or delete.'),
});

const holidaySlotDataSchema = type({
  type: type("'holiday'").configure(
    meta('Public holiday, normally created by holiday sync. Writes with a non-`manual` `source` are rejected with 400.')
  ),
  ...commonDataFields,
  'absenceType?': stringField(
    'Holiday absence type key; resolved from the tenant default when omitted. Unknown or inactive types are rejected with 400.',
    ['holiday']
  ),
  'isAllDay?': isAllDayField,
  'countryCode?': stringField('ISO 3166-1 alpha-2 country code of the holiday source.', ['CH']),
  'stateCode?': stringField('ISO 3166-2 sub-division code (state/canton) of a regional holiday.', ['ZH']),
  'holidayTypes?': type('string[]').configure(meta('Holiday classifications reported by the holiday source.')),
});

const openSlotDataSchema = type({
  type: stringField('Any other slot type key, e.g. a custom slot type. Remaining fields are stored as sent.'),
  '[string]': 'unknown',
});

export const calendarSlotDataSchema = externalBookingSlotDataSchema
  .or(teamMeetingSlotDataSchema)
  .or(workLocationSlotDataSchema)
  .or(locationOverrideSlotDataSchema)
  .or(absenceSlotDataSchema)
  .or(holidaySlotDataSchema)
  .or(openSlotDataSchema)
  .configure(
    meta(
      'Slot payload; `type` selects the behaviour. Fields marked response-only are stamped by the server and ignored on write; `onlineMeeting` inside `data` is stripped and never persisted. The generic variant is the catch-all and stays last.'
    )
  );

export const calendarSlotPartialDataSchema = externalBookingSlotDataSchema
  .partial()
  .or(teamMeetingSlotDataSchema.partial())
  .or(workLocationSlotDataSchema.partial())
  .or(locationOverrideSlotDataSchema.partial())
  .or(absenceSlotDataSchema.partial())
  .or(holidaySlotDataSchema.partial())
  .or(openSlotDataSchema.partial())
  .configure(
    meta(
      'Partial slot payload for updates: every field, `type` included, is optional. Sent fields are merged into the stored data, omitted fields and custom property keys stay unchanged, and `null` for a custom property key removes it. Field descriptions match the create payload.'
    )
  );
